import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import KYCModalScreen from '../../../KYCModalScreen';
import {alertActions} from '../../../../../../state/actions/alert.actions';
import {bankingActions} from '../../../../../../state/actions/banking.actions';
import LegalRepresentativeInformation from '../../substeps/LegalRepresentativeInformation';
import LegalRepresentativeDocuments from '../../substeps/LegalRepresentativeDocuments';
import {StyledKYCModalSpin} from '../../../KYCModalScreen/StyledKYCModalScreen';
import {useBankingCompanyState, useKYCCompanyHeaders} from '../../../../../../hooks';
import {bankingDocumentTypes, bankingUserTypesConstants} from '../../../../../../constants';
import {kycHelpers, objectHelpers} from '../../../../../../utils/helpers';

const {LEGAL_REPRESENTATIVE} = bankingUserTypesConstants;
const {IDENTIFY_CARD} = bankingDocumentTypes;

const LegalRepresentative = ({
  onFinish,
  ...rest
}) => {
  const dispatch = useDispatch();
  const companyState = useBankingCompanyState();
  const kycHeaders = useKYCCompanyHeaders();
  const invitedUser = useSelector(state => state.banking.invitedUser);
  const [trans] = useTranslation(['main', 'quickStart']);

  const [step, setStep] = useState(1);
  const [confirmError, setConfirmError] = useState('');
  const [isCompanyLR, setIsCompanyLR] = useState(false);
  const [user, setUser] = useState(null);
  const [disabledOk, setDisabledOk] = useState(false);
  const [loading, setLoading] = useState(true);

  const informationFormRef = useRef(null); // step 1
  const documentsFormRef = useRef(null); // step 2
  // Names the backend has already accepted, so a resubmit after a partial failure does not
  // re-send them. Held in a ref because handleDocumentsSubmit re-runs from scratch each time.
  const acceptedDocumentsRef = useRef(new Set());

  // Steps are 1 information, 2 documents, 3 terminal. Only the terminal step has no OK --
  // the document-type substep that used to sit at 2 is gone (H1MVP-4653).
  const enabledOk = useMemo(() => step !== 3, [step]);

  const t = (key) => trans(`quickStart:kyc.${key}`);

  const finishLoading = () => setLoading(false);

  const getUser = () => {
    dispatch(bankingActions.getCardsUsers({
      query: {user_type: LEGAL_REPRESENTATIVE},
      successCallback: (users) => {
        users.length > 0 && setUser(users[0]);
        finishLoading();
      },
      errorCallback: finishLoading,
      headers: kycHeaders
    }));
  }

  const enabledCancel = useMemo(() => Boolean(step === 3 && confirmError), [confirmError, step]);

  useEffect(() => {
    const state = objectHelpers.getObjProp(companyState, 'state');
    if (state) {
      const states = {
        'lr_created': 2,
        'lr_submitting_documents': 2,
        'lr_documents_submitted': 3
      }
      const activeStep = states[state] || 1;

      if (activeStep === 3) {
        onFinish?.();
      } else {
        if (activeStep !== step) setStep(activeStep);
        if (activeStep > 1 && !isCompanyLR) setIsCompanyLR(true);
      }
    }
  }, [companyState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step !== 1 && user === null) getUser();
  }, [step]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user && invitedUser === null) {
      setLoading(true);
      dispatch(bankingActions.getInvitationDetails({
        id: user.id,
        successCallback: (invitation) => {
          const accepted = invitation.is_accepted;
          if (accepted) {
            setStep(4);
            setIsCompanyLR(true);
          }
          finishLoading();
        },
        errorCallback: finishLoading,
        headers: kycHeaders
      }));
    }
  }, [invitedUser, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const onOk = () => {
    const actions = {
      1: () => informationFormRef.current.click(),
      2: () => documentsFormRef.current.submit()
    }
    if (actions.hasOwnProperty(step)) actions[step]();
  }

  const onCancel = () => {
    step !== 1 && setStep(1);
    confirmError && setConfirmError('');
  }

  const increaseStep = () => setStep(step + 1);

  const handleDocumentsSubmit = (fields, successCallback, errorCallback) => {
    // The rep no longer picks a document type; the LR always uploads an identity card.
    const documents = Object.keys(fields)
      .filter((key) => fields[key] !== undefined)
      .map((key) => ({name: key, file: fields[key], documentTypeId: IDENTIFY_CARD}));
    if (documents.length === 0) {
      alertActions.error(t('validation.pleaseUploadDocument'));
      errorCallback && errorCallback();
      return;
    }

    kycHelpers.uploadDocumentsSequentially({
      documents,
      userId: user?.id,
      createDocument: (data, successCallback, errorCallback) => dispatch(bankingActions.createDocument({data, successCallback, errorCallback, headers: kycHeaders})),
      accepted: acceptedDocumentsRef.current,
      onSuccess: () => {
        successCallback?.();
        onFinish?.();
      },
      onError: ({name, response}) => {
        errorCallback?.(kycHelpers.getFileFormErrors({name, response, t: trans}));
        dispatch(bankingActions.getCompanyState({headers: kycHeaders}));
      },
    });
  }

  const handleLRInformationFinish = () => increaseStep();

  const handleUpdateLROkEnable = (enable) => {
    if (step === 1 && enable === disabledOk) setDisabledOk(!enable);
  }

  const getStepContent = () => {
    const screens = {
      1: (
        <LegalRepresentativeInformation
          className='column-form-content'
          onFinish={handleLRInformationFinish}
          handleUpdateOkEnable={handleUpdateLROkEnable}
          handleLoadUser={getUser}
          user={user}
          ref={informationFormRef}
        />
      ),
      2: (
        // variant is left to its 'id-card' default, which is the IDENTIFY_CARD type uploaded above
        <LegalRepresentativeDocuments
          onSubmit={handleDocumentsSubmit}
          ref={documentsFormRef}
        />
      )
    }
    return screens[step] || null;
  }

  const stepContent = getStepContent();

  return (
    <KYCModalScreen
      enabledCancel={enabledCancel}
      enabledOk={enabledOk}
      okProps={{disabled: disabledOk}}
      onCancel={onCancel}
      onOk={onOk}
      {...rest}
    >
      <StyledKYCModalSpin
        size='small'
        spinning={loading}
      >
        {stepContent}
      </StyledKYCModalSpin>
    </KYCModalScreen>
  );
}

LegalRepresentative.propTypes = {
  onFinish: PropTypes.func
}

export default LegalRepresentative;
